import { Button, Divider, Modal, Loader } from "@src/components/atoms";
import { CardContainer } from "@src/components/cards";
import { normalizeEmptyStringsToNull } from "@src/components/forms/admin/AdminHelpers";
import { CharityForm } from "@src/components/forms/admin/CharityForm";
import { dateFormatterUTC } from "@src/constants/convertions";
import AdminHooks from "@src/context/hooks/adminHooks";
import { useAuthContext } from "@src/context";
import { useState } from "react";

export const CharitiesSection = ({ customer }) => {
  const auth = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedCharity, setSelectedCharity] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const mutationCallback = (status) => {
    console.log("saved charity changes");
  };

  const updateMutation = AdminHooks.useUpdateCharity(mutationCallback);
  const createMutation = AdminHooks.useCreateCharity(mutationCallback);
  const deleteMutation = AdminHooks.useDeleteCharity(mutationCallback);

  const handleDeleteCharity = async (charity) => {
    setSubmitted(false);
    setSubmitting(true);
    setError(null);
    try {
      if (
        window.confirm(
          `Delete ${charity.name} from ${customer.customerCode} permenantly, and remove it from corresponding operators?`,
        )
      ) {
        await deleteMutation.mutateAsync(charity.id);
      }
      setSubmitted(true);
    } catch (err) {
      setError("Failed to delete charity");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitChanges = async (data) => {
    setSubmitted(false);
    setSubmitting(true);
    setError(null);

    const formatted = normalizeEmptyStringsToNull(data);
    try {
      if (isAdding) {
        await createMutation.mutateAsync(formatted);
      } else {
        await updateMutation.mutateAsync(formatted);
      }
      setSubmitted(true);
    } catch (err) {
      setError("Failed to submit charity changes");
    } finally {
      setSelectedCharity(null);
      setSubmitting(false);
      !!isEditing && setIsEditing(false);
      !!isAdding && setIsAdding(false);
    }
  };

  const {
    data: charities,
    isLoading,
    isPending,
    isSettled,
    isError,
    //error: errorAll,
  } = AdminHooks.useGetCharities(auth.activeRole, customer.customerCode);

  return (
    <div className="p-4">
      {!!submitted && (
        <div className="grow text-green-500 border border-1-green rounded-2xl p-2 bg-green-100">
          <p>Your changes have been submitted successfully.</p>
        </div>
      )}
      {!!error && (
        <div className="grow text-red-500 border border-1-red rounded-2xl p-2 bg-red-100">
          <p>{error}</p>
        </div>
      )}
      {isLoading && <Loader size="2xl" />}

      {!isLoading && !charities.length ? (
        <>
          <div className="p-4">No charity set for this customer</div>

          <div className="w-48">
            <Button icon={"plus"} block={false} onClick={setIsAdding} secondary>
              Add charity
            </Button>
          </div>
        </>
      ) : (
        charities &&
        charities.map((charity, index) => (
          <div key={`${charity.name}-${index}`} className="py-4">
            {selectedCharity?.name !== charity.name ? (
              <CardContainer key={charity.name} padding={4}>
                <h4 className="pb-2">{charity.name}</h4>
                {Object.entries(charity).map(([key, value], index) => {
                  const result = key.includes("Date")
                    ? dateFormatterUTC(value)
                    : Array.isArray(value)
                      ? value.length
                      : String(value);

                  return (
                    <div key={key} className="flex ">
                      <div className="min-w-64 font-bold">{key}</div>
                      <div>{result}</div>
                    </div>
                  );
                })}
                <div className="flex w-48">
                  <Button
                    icon={"feedback"}
                    block={false}
                    secondary
                    onClick={() => setSelectedCharity(charity)}
                  >
                    Edit
                  </Button>
                  <div>
                    <Button
                      icon={"close"}
                      color="red"
                      block={false}
                      secondary
                      onClick={() => handleDeleteCharity(charity)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContainer>
            ) : (
              <CardContainer>
                <div className="p-2">
                  <h4 className="pt-2">Editing an existing charity</h4>
                  {charity?.id && <p className="pt-2">id: {charity?.id}</p>}
                  <CharityForm
                    key={charity.name}
                    data={selectedCharity}
                    onSubmit={(data) =>
                      handleSubmitChanges({ id: charity?.id, ...data })
                    }
                  />
                </div>
              </CardContainer>
            )}
          </div>
        ))
      )}

      {isAdding && (
        <Modal
          open={isAdding}
          handleClose={() => setIsAdding(false)}
          children={
            <div className="py-4 text-center">
              <h4>New Charity details</h4>
              <CharityForm
                key={"new-charity"}
                data={null}
                isNew={true}
                onSubmit={(data) =>
                  handleSubmitChanges({ customerId: customer.id, ...data })
                }
              />
            </div>
          }
        />
      )}
    </div>
  );
};
